import { useAsyncEffect } from '@/hooks';
import { downloadBlob, paginate } from '@/lib/utils';
import { useStores } from '@/store';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { observer } from 'mobx-react';
import React, { useContext, useMemo, useState } from 'react';
import { Link } from 'react-router';
import { Form, Grid, Pagination, Popup } from 'semantic-ui-react';
import { EmployeeStatus, ID } from 'sol-data';
import { OnboardingFormEmployee } from 'sol-data/dist/entities/onboarding-form-employee';
import { TemporaryEmployee } from 'sol-data/dist/entities/temporary-employee';
import { EmployeeStatusColor } from '../DepartmentEmployeesList/utils';
import { EntityList } from '../EntityList';
import { Item, ListColumn } from '../ListLayout';
import { MultipleFilesPreviewModal } from '../MultipleFilesPreviewModal';
import AppContext from '@/AppContext';
import { SignatureModal } from '../SignatureModal/SignatureModal';

const RESULTS_PER_PAGE = 10;

interface OnboardingFormItem extends Item {
  id: number;
  title: string;
  createdBy: string;
  createdOn: string;
  signedAt: string | undefined;
  signature: string | undefined;
  status: EmployeeStatus;
  hasUploadedFiles: boolean;
  employeeId: string | number;
  temporaryEmployeeId: string | number;
}

interface EmployeeOnboardingFormsProps {
  employeeId: number;
}

export const EmployeeOnboardingForms = observer(({ employeeId }: EmployeeOnboardingFormsProps) => {
  const { onboardingFormStore, onboardingFormEmployeeStore } = useStores();
  const [currentPage, setCurrentPage] = useState(1);
  const [shouldShowFilePreviewModal, setShouldShowFilePreviewModal] = useState(false);
  const [selectedOnboardingFormEmployeeId, setSelectedOnboardingFormEmployeeId] =
    useState<ID<OnboardingFormEmployee>>();
  const [temporaryEmployeeId, setTemporaryEmployeeId] = useState<ID<TemporaryEmployee>>();
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [signature, setSignature] = useState<string>();
  const appContext = useContext(AppContext);
  const isAdmin = appContext?.isAdmin;

  const fetchData = async (shouldIncludeDeleted: boolean) => {
    await onboardingFormStore.fetchAllForEmployee(employeeId!, shouldIncludeDeleted);
  };

  useAsyncEffect(async () => {
    await fetchData(includeDeleted);

    return () => {
      onboardingFormStore.clearFilters();
      onboardingFormStore.clear();
    };
  }, [employeeId]);

  useAsyncEffect(async () => {
    if (temporaryEmployeeId) {
      await onboardingFormEmployeeStore.fetchByTemporaryEmployeeId(temporaryEmployeeId);
    }
  }, [temporaryEmployeeId]);

  const onboardingForms = onboardingFormStore.filtered.map(
    ({ id, title, createdByUser, insertDatetime, deleteDatetime, employees }) => {
      const employee = employees?.[0];

      return {
        id,
        title,
        createdBy: createdByUser?.fullName ?? 'Anonymous',
        createdOn: insertDatetime.format('MMMM Do [at] h:mmA'),
        deletedOn: deleteDatetime?.format('MMMM Do [at] h:mmA'),
        deleteDatetime,
        signedAt: employee?.signedAt?.format('MMMM Do [at] h:mmA'),
        signature: employee?.signature,
        hasUploadedFiles: employee?.hasUploadedFiles ?? false,
        employeeId: employee?.id ?? '',
        temporaryEmployeeId: employee?.user?.temporaryEmployee?.id ?? '',
        status:
          employees?.length && employees[0]?.isCompleted
            ? EmployeeStatus.COMPLETE
            : EmployeeStatus.INCOMPLETE,
        files: employee?.files ?? [],
      };
    },
  );

  const onboardingForm = onboardingForms.find(
    ({ employeeId }) => employeeId === selectedOnboardingFormEmployeeId,
  );
  const selectedOnboardingFormEmployee = onboardingFormEmployeeStore.values.find(
    ({ id }) => id === selectedOnboardingFormEmployeeId,
  );

  const columns: ListColumn<OnboardingFormItem>[] = [
    {
      name: 'Document Title',
      key: 'title',
      width: { sm: 5, lg: 5 },
      render: ({ id, title }) => (
        <Link to={`/onboarding/forms/templates/${id}`} target="_blank">
          {title}
        </Link>
      ),
    },
    {
      name: 'Created By',
      key: 'createdBy',
      textAlign: 'center',
      width: { sm: 2, lg: 2 },
      render: ({ createdBy }) => {
        return <div style={{ textAlign: 'center' }}>{createdBy}</div>;
      },
    },
    {
      name: 'Posted On',
      key: 'postedOn',
      textAlign: 'center',
      width: { sm: 3, lg: 3 },
      render: ({ createdOn }) => <div style={{ textAlign: 'center' }}>{createdOn}</div>,
    },
    {
      name: 'Signed At',
      key: 'signedAt',
      textAlign: 'center',
      width: { sm: 3, lg: 3 },
      render: ({ signedAt, signature }) => (
        <div style={{ textAlign: 'center' }}>
          {signedAt ? (
            <a href="#" onClick={() => setSignature(signature)}>
              {signedAt ? signedAt : 'N/A'}
            </a>
          ) : (
            <div>N/A</div>
          )}
        </div>
      ),
    },
    {
      name: 'Status',
      key: 'status',
      textAlign: 'center',
      width: { sm: 2, lg: 2 },
      render: ({ status }) => (
        <div
          style={{
            textAlign: 'center',
            fontWeight: 700,
            color:
              status === EmployeeStatus.COMPLETE
                ? EmployeeStatusColor.Complete.color
                : EmployeeStatusColor.Incomplete.color,
          }}
        >
          {status}
        </div>
      ),
    },
    {
      name: 'File',
      key: 'file',
      textAlign: 'center',
      width: { sm: 1, lg: 1 },
      render: ({ hasUploadedFiles, employeeId, temporaryEmployeeId }) => (
        <Popup
          content={
            hasUploadedFiles
              ? 'View the files the employee uploaded'
              : 'No files uploaded by employee'
          }
          style={{ textAlign: 'center' }}
          trigger={
            <FontAwesomeIcon
              icon={['fas', 'eye']}
              color={hasUploadedFiles ? '#2483c8' : '#ccc'}
              style={{ cursor: 'pointer' }}
              onClick={() => {
                setSelectedOnboardingFormEmployeeId(employeeId);
                setTemporaryEmployeeId(temporaryEmployeeId);
                setShouldShowFilePreviewModal(true);
              }}
            />
          }
        />
      ),
    },
  ];

  const handleIncludeDeletedChange = async (checked: boolean) => {
    setIncludeDeleted(checked);
    await fetchData(checked);
  };

  return (
    <>
      <EntityList<OnboardingFormItem>
        title="Onboarding Documents"
        searchLabel="Search by document or file name"
        columns={columns}
        items={paginate(onboardingForms, RESULTS_PER_PAGE, currentPage)}
        titleProp="title"
        onSearchChange={(filter) => onboardingFormStore.setFilter(filter)}
        onDismiss={() => onboardingFormStore.clearError()}
        onRefresh={() => fetchData(includeDeleted)}
        errorMessage={onboardingFormStore.errorMessage}
        isLoading={onboardingFormStore.isLoading}
        noNewButton
        searchHeaderContent={
          <Form
            style={{
              display: 'flex',
              flexDirection: 'row',
              gap: 10,
              width: '100%',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Form.Field style={{ margin: 0 }}>
              <Form.Checkbox
                label="Show archived forms"
                checked={includeDeleted}
                onChange={(_, { checked }) => handleIncludeDeletedChange(checked || false)}
              />
            </Form.Field>
          </Form>
        }
      />
      <Grid centered>
        <Grid.Row>
          <Pagination
            activePage={currentPage}
            totalPages={Math.ceil(onboardingForms.length / RESULTS_PER_PAGE)}
            onPageChange={(_, { activePage }) => setCurrentPage(activePage as number)}
            siblingRange={2}
            boundaryRange={0}
            firstItem={{ content: 'First' }}
            lastItem={{ content: 'Last' }}
            ellipsisItem={null}
            prevItem={null}
            nextItem={null}
          />
        </Grid.Row>
      </Grid>

      {shouldShowFilePreviewModal && (
        <MultipleFilesPreviewModal
          visible={shouldShowFilePreviewModal}
          onClose={() => {
            setShouldShowFilePreviewModal(false);
            setSelectedOnboardingFormEmployeeId(undefined);
            setTemporaryEmployeeId(undefined);
          }}
          formTitle={onboardingForm?.title || 'Form'}
          files={onboardingForm?.files || []}
          signature={selectedOnboardingFormEmployee?.signature}
          fetchUrl={async (fileId: number) => {
            const url = await onboardingFormEmployeeStore.fetchUrl(
              selectedOnboardingFormEmployeeId!,
              fileId,
            );
            return url;
          }}
          fetchBlob={async (fileId: number) => {
            const { file } = await onboardingFormEmployeeStore.download(
              selectedOnboardingFormEmployeeId!,
              fileId,
            );
            return file;
          }}
          handleDownload={async (fileId: number, fileName?: string) => {
            if (fileName) {
              const { file } = await onboardingFormEmployeeStore.download(
                selectedOnboardingFormEmployeeId!,
                fileId,
              );

              downloadBlob(file, fileName);
            }
          }}
          handleArchive={async (fileId: number) => {
            if (selectedOnboardingFormEmployeeId && temporaryEmployeeId) {
              await onboardingFormEmployeeStore.deleteFile(
                selectedOnboardingFormEmployeeId,
                fileId,
              );

              const onboardingFormEmployees =
                await onboardingFormEmployeeStore.fetchByTemporaryEmployeeId(temporaryEmployeeId);

              const currentEmployee = onboardingFormEmployees.find(
                ({ id }) => id === selectedOnboardingFormEmployeeId,
              );

              setSelectedOnboardingFormEmployeeId(currentEmployee?.id);

              await fetchData(includeDeleted);
            }
          }}
        />
      )}

      {signature && (
        <SignatureModal
          visible={!!signature}
          signature={signature}
          onClose={() => setSignature(undefined)}
        />
      )}
    </>
  );
});
